import { useQuery } from '@tanstack/react-query';
import type { AppointedOfficer, LocalGovernmentProfile } from '@munifocus/contracts-gov';
import { api } from './api-client';

/**
 * The local-government registry, read-only.
 *
 * `GET /local-governments` is already scoped server-side to where the caller may
 * open a file — their appointments, and any seat (a Clerk's, say) that carries
 * `file:create` — so an officer appointed at one municipality sees one row, not
 * all 77, and nothing here filters. Writes belong to the Admin Portal and there is
 * deliberately no mutation hook in this module.
 *
 * Cached for the session, on the same reasoning as the Stage 1 taxonomies: the
 * registry changes a few times a year, and an officer filling in one intake form
 * should not re-fetch it between fields.
 */

const STALE_MS = 5 * 60 * 1000;

export const localGovernmentKeys = {
  list: ['local-governments'] as const,
  officers: (id: string) => ['local-governments', id, 'officers'] as const,
};

export function useLocalGovernments() {
  return useQuery({
    queryKey: localGovernmentKeys.list,
    queryFn: async () => {
      const { data } = await api.get<LocalGovernmentProfile[]>('/local-governments');
      return data;
    },
    staleTime: STALE_MS,
  });
}

/**
 * The appointment roster at one local government — who a file may be assigned to.
 *
 * Both access types come back. Staff are not assignable as the working officer,
 * but they are who a file gets routed to for external action, so the caller sees
 * them and decides on `accessType` rather than being handed a pre-filtered list.
 *
 * Disabled until a local government is chosen: an officer picker on an
 * unanchored file has nothing to draw from.
 */
export function useLocalGovernmentOfficers(localGovernmentId: string | null | undefined) {
  return useQuery({
    queryKey: localGovernmentKeys.officers(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<AppointedOfficer[]>(
        `/local-governments/${localGovernmentId}/officers`,
      );
      return data;
    },
    enabled: !!localGovernmentId,
    staleTime: STALE_MS,
  });
}
